import { useEffect, useRef, useState } from "react";
import { IdCard, Image as ImageIcon, Pencil, ShieldCheck, Trash2, Upload } from "lucide-react";
import "./Technicians.css"; 
import { adminApi } from "../../utils/adminApi";
export default function Technicians() {

  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  /* ================= FETCH TECHNICIANS ================= */

  const fetchTechs = async () => {
    try {
      const res = await adminApi.get("/api/technicians");
      setTechnicians(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Unable to load technicians");
    }
  };

  useEffect(() => {
    fetchTechs();
  }, []);

  /* ================= VALIDATION ================= */

  const validateForm = () => {

    if (username.trim().length < 3) {
      setError("Username must be at least 3 characters");
      return false;
    }

    const phoneRegex = /^[0-9]{10}$/;

    if (!phoneRegex.test(phone)) {
      setError("Phone number must be exactly 10 digits");
      return false;
    }

    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setError("Enter a valid email address");
        return false;
      }
    }

    setError("");
    return true;
  };

  const resetForm = () => {
    setUsername("");
    setPhone("");
    setEmail("");
    setEmployeeId("");
    setPhotoUrl("");
    setPhotoFile(null);
    setPhotoPreview("");
    setIsDragging(false);
    setIsUploadingPhoto(false);
    setIsVerified(false);
    setEditingId(null);
  };

  /* ================= INPUT HANDLERS ================= */

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (error) setError("");
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, ""));
    if (error) setError("");
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError("");
  };

  const handleEmployeeIdChange = (e) => {
    setEmployeeId(e.target.value);
    if (error) setError("");
  };

  const setPhotoFromFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    setError("");
    if (photoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(file);
    setPhotoUrl("");
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handlePhotoInputChange = (event) => {
    const file = event.target.files?.[0];
    setPhotoFromFile(file);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    setPhotoFromFile(file);
  };

const uploadPhotoIfNeeded = async () => {

  if (!photoFile) return photoUrl.trim();

  const formData = new FormData();

  formData.append("photo", photoFile);

  setIsUploadingPhoto(true);

  try {

    const response = await adminApi.post(
      "/api/technicians/upload-photo",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data?.imageUrl || "";

  } catch (uploadError) {

    throw new Error(
      uploadError.response?.data?.error ||
      "Technician photo upload failed"
    );

  } finally {

    setIsUploadingPhoto(false);

  }
};

  /* ================= ADD / UPDATE TECHNICIAN ================= */

  const addTech = async () => {

    if (!validateForm()) return;

    try {
      const uploadedPhotoUrl = await uploadPhotoIfNeeded();
      const finalPhotoUrl = uploadedPhotoUrl || photoUrl.trim();

      if (editingId) {

        await adminApi.put(`/api/technicians/${editingId}`, {
          name: username,
          phone,
          email: email.trim(),
          employeeId: employeeId.trim(),
          photoUrl: finalPhotoUrl,
          isVerified: Boolean(isVerified),
        });

        setEditingId(null);

      } else {

        await adminApi.post("/api/technicians", {
          name: username,
          phone,
          email: email.trim(),
          employeeId: employeeId.trim(),
          photoUrl: finalPhotoUrl,
          isVerified: Boolean(isVerified),
        });

      }

      resetForm();

      fetchTechs();

    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          saveError.message ||
          "Unable to save technician"
      );

    }

  };

  /* ================= EDIT ================= */

  const editTech = (tech) => {

    setUsername(tech.name || "");
    setPhone(tech.phone || "");
    setEmail(tech.email || "");
    setEmployeeId(tech.employeeId || "");
    setPhotoUrl(tech.photoUrl || "");
    setIsVerified(Boolean(tech.isVerified));
    setEditingId(tech._id);
    setError("");

  };

  /* ================= DELETE ================= */

  const deleteTech = async (id) => {

    try {

      await adminApi.delete(`/api/technicians/${id}`);
      fetchTechs();

    } catch {

      setError("Unable to delete technician");

    }

  };

  /* ================= UI ================= */

  return (

    <div className="admin-dashboard">

      <h2 className="admin-title">Add Technician</h2>

      <div className="admin-card">

     <div className="admin-form">

  {/* LEFT SIDE */}
  <div className="form-left">
    <input type="text" placeholder="Technician Username" value={username} onChange={handleUsernameChange} />
    <input type="text" placeholder="Phone Number" maxLength="10" value={phone} onChange={handlePhoneChange} />
    <input type="email" placeholder="Technician Email (for OTP)" value={email} onChange={handleEmailChange} />
    <input type="text" placeholder="Technician ID" value={employeeId} onChange={handleEmployeeIdChange} />
  </div>

  {/* RIGHT SIDE */}
  <div className="form-right">
    <div
      className={`drag-photo-box ${isDragging ? "active" : ""} ${photoPreview ? "has-image" : ""}`}
      onClick={() => fileInputRef.current?.click()}
      onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="drag-photo-input"
        onChange={handlePhotoInputChange}
      />

      {photoPreview ? (
        <img src={photoPreview} alt="preview" className="drag-photo-preview" />
      ) : (
        <div className="drag-photo-content">
          <Upload size={18} />
          <span>Drag & drop technician photo here</span>
          <small>or click to choose a file</small>
        </div>
      )}
    </div>
  </div>

</div>

{/* BELOW */}
<label className="admin-checkbox">
  <input
    type="checkbox"
    checked={isVerified}
    onChange={(e) => setIsVerified(e.target.checked)}
  />
  <span>Verified badge</span>
</label>

<button className="small-btn" onClick={addTech}>
  Add Technician
</button>

        {error ? <div className="admin-error">{error}</div> : null}

        <div className="admin-list">

          {technicians.length === 0 ? (
            <div className="admin-empty">No technicians found</div>
          ) : null}

          {technicians.map((tech) => (

            <div key={tech._id} className="admin-list-item">

              <div className="tech-card">
                <div className="tech-avatar">
                  {tech.photoUrl ? (
<img
  src={tech.photoUrl}
  alt={tech.name}
/>
                  ) : (
                    <ImageIcon size={18} />
                  )}
                </div>

                <div className="tech-meta">
                  <span className="tech-name">
                    {tech.name} - {tech.phone}
                    {tech.isVerified ? (
                      <span className="verified-pill">
                        <ShieldCheck size={14} />
                        Verified
                      </span>
                    ) : null}
                  </span>

                  <span className="tech-id">
                    <IdCard size={14} />
                    {tech.employeeId || "No ID"}
                  </span>
                </div>
              </div>

              <div className="admin-actions">

                <button
                  className="edit-btn"
                  onClick={() => editTech(tech)}
                >
                  <Pencil size={16} />
                </button>

                <button
                  className="delete-btn"
                  onClick={() => deleteTech(tech._id)}
                >
                  <Trash2 size={16} />
                </button>

              </div>

            </div>

          ))}

        </div>

      </div>

    </div>

  );

}
